import { useContext, useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { Cursor3DContext } from '../../three/Cursor3DContext';

// CursorFollower — the 120% detail. A small 10px hairline circle that
// follows the cursor with spring physics, AND:
//  - scales to 24px when over any clickable element
//  - turns gold (#b8a572) when over a link
//  - leaves a 3-dot fading trail with ~70ms spacing
//  - hides the native cursor
// This is the moment people screenshot. Disabled on touch / narrow
// viewports and for `prefers-reduced-motion` users.

const TRAIL_LEN = 3;
const TRAIL_DELAY = 70; // ms

const isInteractive = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA') return true;
  if ((el as HTMLElement).getAttribute('role') === 'button') return true;
  if ((el as HTMLElement).hasAttribute('data-cursor')) return true;
  return false;
};

const isLink = (el: Element | null): boolean => {
  if (!el) return false;
  if (el.tagName === 'A') return true;
  return false;
};

export function CursorFollower() {
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [overLink, setOverLink] = useState(false);
  const trailRef = useRef<{ x: number; y: number; t: number }[]>([]);
  const cursor3d = useContext(Cursor3DContext);

  const mx = useMotionValue(-100);
  const my = useMotionValue(-100);

  // Spring lag — stiffness 150, damping 20
  const sx = useSpring(mx, { stiffness: 150, damping: 20, mass: 0.8 });
  const sy = useSpring(my, { stiffness: 150, damping: 20, mass: 0.8 });

  // Scale the dot from 10px -> 24px when hovering an interactive
  const size = useTransform(sx, () => (hovering ? 24 : 10));

  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (max-width: 860px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!mq.matches && !reduced.matches);
    update();
    mq.addEventListener('change', update);
    reduced.addEventListener('change', update);

    if (!enabled) {
      mq.removeEventListener('change', update);
      reduced.removeEventListener('change', update);
      // While disabled, reset cursor3d refs toward center so 3D layers
      // return to neutral instead of stalling at last position.
      if (cursor3d) {
        const damp = () => {
          cursor3d.cx.current = cursor3d.cx.current * 0.92;
          cursor3d.cy.current = cursor3d.cy.current * 0.92;
          cursor3d.active.current = false;
          id = requestAnimationFrame(damp);
        };
        let id = requestAnimationFrame(damp);
        return () => cancelAnimationFrame(id);
      }
      return;
    }

    let raf = 0;
    function onMove(e: MouseEvent) {
      mx.set(e.clientX);
      my.set(e.clientY);
      const target = e.target as Element | null;
      const inter = isInteractive(target);
      setHovering(inter);
      setOverLink(isLink(target));

      // Publish to 3D context — raw values, no smoothing here.
      // The 3D scene applies its own lerp. Normalized -1..1 in viewport.
      if (cursor3d) {
        const normX = (e.clientX / window.innerWidth) * 2 - 1;
        const normY = -((e.clientY / window.innerHeight) * 2 - 1);
        cursor3d.cx.current = normX;
        cursor3d.cy.current = normY;
        cursor3d.active.current = true;
      }

      // Record trail point (with timestamp)
      trailRef.current.push({ x: e.clientX, y: e.clientY, t: performance.now() });
      // Trim old points
      const cutoff = performance.now() - TRAIL_DELAY * TRAIL_LEN;
      while (trailRef.current.length > 0 && trailRef.current[0].t < cutoff) {
        trailRef.current.shift();
      }
      // Trigger a redraw of the trail via state nudge
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTrailTick((t) => (t + 1) % 1024));
    }
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      mq.removeEventListener('change', update);
      reduced.removeEventListener('change', update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, mx, my, cursor3d]);

  // re-render the trail each mousemove via raf
  const [, setTrailTick] = useState(0);

  if (!enabled) return null;

  // Trail dots: render a copy of the trailRef's points. We re-render on
  // each animation frame, so the dot positions are slightly stale by
  // design (motion-blur feel).
  const now = performance.now();
  const trail = trailRef.current
    .filter((p) => now - p.t < TRAIL_DELAY * TRAIL_LEN)
    .map((p, i) => {
      const age = now - p.t;
      const fade = 1 - age / (TRAIL_DELAY * TRAIL_LEN);
      return { x: p.x, y: p.y, fade: Math.max(0, fade), key: `${p.t}-${i}` };
    })
    .reverse(); // oldest first -> newest last

  return (
    <>
      {/* Trail dots — fading motion-blur effect */}
      {trail.map((t, i) => (
        <span
          key={t.key}
          aria-hidden="true"
          className="cursor-trail"
          style={{
            transform: `translate(${t.x}px, ${t.y}px)`,
            opacity: t.fade * 0.35,
            width: 6 - i,
            height: 6 - i,
            background: overLink ? '#b8a572' : '#f5f0e8',
          }}
        />
      ))}

      {/* Main follower dot */}
      <motion.div
        className="cursor-follower"
        style={{
          x: sx,
          y: sy,
          width: size,
          height: size,
          background: overLink ? '#b8a572' : 'var(--accent)',
          opacity: hovering ? 0.9 : 0.5,
        }}
        aria-hidden="true"
      />
    </>
  );
}
